import React, { useEffect, useRef, useState } from "react";

import HttpManager from "../../../services/HttpManager";
import { Actividad, ActividadNueva } from "../../../services/v2/prospects.service";
import { cuandoViene, errorTexto, fechaDia, hora, localMxAIso } from "./salesHelpers";
import FormCita, { armarActividad, HoraElegida, horaCompleta, ModalCita, PasoHora, tituloCita } from "./formCita";

/** "Visita" ahora es Cita de Matrícula (Esteban, 2026-10-13; en Monterrey "Cita de Inscripción"), con plantel o digital. */
export const TIPOS: { key: string; label: string }[] = [
	{ key: "llamar", label: "Llamar" }, { key: "cita", label: "Cita de Matrícula" }, { key: "info", label: "Mandar información" },
	{ key: "pago", label: "Seguimiento de pago" }, { key: "otro", label: "Otro" },
];
const tipoLabel = (key: string, plantelId?: number | null) => key === "cita" ? tituloCita(plantelId) : TIPOS.find((t) => t.key === key)?.label || key;
const RESULTADOS: { key: string; label: string }[] = [
	{ key: "se_hizo", label: "Se hizo" }, { key: "no_contesto", label: "No contestó" }, { key: "reagendo", label: "Reagendó" }, { key: "no_interesa", label: "Ya no le interesa" },
];

export interface Actividades { abiertas: Actividad[]; cerradas: Actividad[]; totalCerradas: number; }

/** Mañana (hora de México), para que el formulario arranque lleno. Sin hora = todo el día (vence 23:59 MX y se enseña solo la fecha). */
const manana = (): string => new Date(Date.now() - 6 * 3600000 + 86400000).toISOString().slice(0, 10);

/** Cuándo vence: "Vencida · ayer 4:00 pm", "Hoy 6:00 pm", "sáb 11 oct". */
export const venceTexto = (a: Actividad): { texto: string; tono: string } => {
	const v = cuandoViene(a.due_dia || a.due_at);
	if (!v) return { texto: "Sin fecha", tono: "despues" };
	return { texto: v.texto.replace(/^Vencido/, "Vencida"), tono: v.tono };
};

/**
 * Nueva actividad (también la usa "Registrar llamada" para el siguiente paso). La hora se pregunta como en la cita: hora
 * exacta, franja o solo el día (Esteban, 2026-10-13: nunca 23:59 implícito). La cita abre el formulario de tres pasos.
 */
export const FormActividad: React.FC<{ campusId: number; prospectId: number; tipoInicial?: string; tituloInicial?: string; plantelId?: number | null;
	onGuardada: () => void; onCancelar: () => void }> = ({ campusId, prospectId, tipoInicial, tituloInicial, plantelId, onGuardada, onCancelar }) => {
	const [tipo, setTipo] = useState<string>(tipoInicial === "visita" ? "cita" : tipoInicial || "llamar");
	const [fecha, setFecha] = useState<string>(manana());
	const [h, setH] = useState<HoraElegida>({ modo: null, hora: "" });
	const [titulo, setTitulo] = useState<string>(tituloInicial || "");
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const crear = async (a: ActividadNueva) => {
		setBusy(true); setError(null);
		try {
			await HttpManager.getInstance().prospectsService.crearActividad(campusId, prospectId, a);
			onGuardada();
		} catch (e) { setError(errorTexto(e)); }
		finally { setBusy(false); }
	};
	const guardar = () => {
		if (!fecha) return;
		if (!horaCompleta(h, fecha)) { setError(h.hora ? "Esa hora ya pasó." : "Elige la hora, o «Sin hora»."); return; }
		try { crear(armarActividad(tipo, fecha, h, { titulo })); } catch (e) { setError(errorTexto(e)); }
	};
	const tipos = (
		<div className="crm-tipos" role="group" aria-label="Tipo de actividad">
			{TIPOS.map((t) => <button key={t.key} className={"crm-tipo" + (tipo === t.key ? " on" : "")} aria-pressed={tipo === t.key} onClick={() => { setTipo(t.key); setError(null); }}>{tipoLabel(t.key, plantelId)}</button>)}
		</div>
	);
	if (tipo === "cita") {
		return (
			<div className="crm-tarjeta crm-form-act">
				<b>Nueva actividad</b>
				{tipos}
				<div style={{ marginTop: 10 }}>
					<FormCita plantelId={plantelId} modo="cita" busy={busy} error={error} onListo={crear} onCancelar={onCancelar} />
				</div>
			</div>
		);
	}
	return (
		<div className="crm-tarjeta crm-form-act">
			<b>Nueva actividad</b>
			{tipos}
			<div className="crm-form">
				<div><label htmlFor="act-fecha">Fecha</label><input id="act-fecha" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} /></div>
				<div className="ancho"><PasoHora value={h} onChange={(v) => { setH(v); setError(null); }} idBase="act" fecha={fecha} /></div>
				<div className="ancho"><label htmlFor="act-para">Para qué</label>
					<input id="act-para" value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Confirmar horario del sábado, mandar precio con kit…"
						onKeyDown={(e) => { if (e.key === "Enter") guardar(); }} /></div>
			</div>
			{error ? <div className="crm-aviso error" style={{ marginTop: 10 }}>{error}</div> : null}
			<div className="crm-botones">
				<button className="crm-b morado" disabled={busy || !fecha} onClick={guardar}>{busy ? "Guardando…" : "Guardar Actividad"}</button>
				<button className="crm-b" onClick={onCancelar}>Cancelar</button>
			</div>
		</div>
	);
};

interface Props {
	campusId: number;
	prospectId: number;
	data: Actividades | null;
	/** Sube cuando el botón de arriba "Agendar actividad" pide abrir el formulario. */
	abrirForm: number;
	onCambio: () => void;
	/** Plantel de la persona: en Monterrey (1) la cita se llama "Cita de Inscripción". */
	plantelId?: number | null;
}

/** Actividades: abiertas (completar con resultado, reagendar) y cerradas, como las Tareas de Zoho. */
const CrmActividades: React.FC<Props> = ({ campusId, prospectId, data, abrirForm, onCambio, plantelId }) => {
	const http = HttpManager.getInstance();
	const [form, setForm] = useState<{ tipo?: string; titulo?: string } | null>(null);
	const [sub, setSub] = useState<"abiertas" | "cerradas">("abiertas");
	const [completar, setCompletar] = useState<number | null>(null);
	const [resultado, setResultado] = useState<string>("se_hizo");
	const [nota, setNota] = useState<string>("");
	const [reagendar, setReagendar] = useState<number | null>(null);
	const [rFecha, setRFecha] = useState<string>(manana());
	const [rH, setRH] = useState<HoraElegida>({ modo: null, hora: "" });
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	// Se cerró la última actividad de una Promesa: hay que poner la siguiente fecha (o cambiar su status arriba).
	const [pideFecha, setPideFecha] = useState<boolean>(false);

	useEffect(() => { if (abrirForm) { setForm({}); setSub("abiertas"); } }, [abrirForm]);
	// Al cambiar de persona (no al montar: "Agendar actividad" de arriba monta esta sección con el formulario abierto).
	const antes = useRef<number>(prospectId);
	useEffect(() => {
		if (antes.current === prospectId) return;
		antes.current = prospectId;
		setForm(null); setCompletar(null); setReagendar(null); setSub("abiertas");
	}, [prospectId]);

	const hacer = async (fn: () => Promise<any>, despues?: () => void) => {
		setBusy(true); setError(null);
		try { await fn(); setCompletar(null); setReagendar(null); setNota(""); onCambio(); if (despues) despues(); }
		catch (e) { setError(errorTexto(e)); }
		finally { setBusy(false); }
	};
	const completarYa = (a: Actividad, siguiente: boolean) => {
		let pide = false;
		return hacer(async () => { const r = await http.prospectsService.completarActividad(campusId, prospectId, a.id, { resultado, nota: nota.trim() || null }); pide = !!r?.data?.pideFecha; },
			() => { if (siguiente) setForm({ tipo: a.tipo, titulo: a.titulo || "" }); else if (pide) setPideFecha(true); });
	};

	if (!data) return <div className="crm-pie">Cargando…</div>;
	return (
		<div>
			{pideFecha ? <ModalCita campusId={campusId} prospectId={prospectId} plantelId={plantelId} modo="cita" permitirSeguimiento
				aviso="Una Promesa lleva fecha: agenda la siguiente cita o un seguimiento (o cámbiale el status arriba)."
				onGuardado={() => { setPideFecha(false); onCambio(); }} onCancelar={() => setPideFecha(false)} /> : null}
			{form ? <FormActividad key={JSON.stringify(form)} campusId={campusId} prospectId={prospectId} tipoInicial={form.tipo} tituloInicial={form.titulo} plantelId={plantelId}
				onGuardada={() => { setForm(null); onCambio(); }} onCancelar={() => setForm(null)} />
				: <button className="crm-b morado" style={{ marginBottom: 12 }} onClick={() => setForm({})}>+ Nueva actividad</button>}
			{error ? <div className="crm-aviso error">{error}</div> : null}
			<div className="crm-subtabs">
				<button className={"crm-subtab" + (sub === "abiertas" ? " on" : "")} onClick={() => setSub("abiertas")}>Abiertas ({data.abiertas.length})</button>
				<button className={"crm-subtab" + (sub === "cerradas" ? " on" : "")} onClick={() => setSub("cerradas")}>Cerradas ({data.totalCerradas.toLocaleString("es-MX")})</button>
			</div>
			{sub === "abiertas" ? (
				<>
					{data.abiertas.map((a) => {
						const v = venceTexto(a);
						return (
							<React.Fragment key={a.id}>
								<div className={"crm-it" + (v.tono === "vencido" ? " vencida" : v.tono === "hoy" ? " hoy" : "")}>
									<div className="crm-ico tarea" aria-hidden="true">{v.tono === "vencido" ? "⏰" : "📅"}</div>
									<div className="tx">
										<b>{a.tipo === "cita" ? tituloCita(plantelId) : a.tipoLabel}</b>{a.titulo ? " · " + a.titulo : ""}
										<div className={"meta" + (v.tono === "vencido" ? " crm-venc" : "")}>
											{v.texto}{a.franjaLabel ? " · " + a.franjaLabel.toLowerCase() : ""}{a.modalidadLabel ? " · " + a.modalidadLabel.toLowerCase() : ""}{a.owner_name ? " · " + a.owner_name : ""}{a.origen === "zoho" ? <span className="crm-orig">Zoho</span> : null}
										</div>
									</div>
									<div className="der">
										<button className="crm-b chico" onClick={() => { setCompletar(completar === a.id ? null : a.id); setReagendar(null); setResultado("se_hizo"); setNota(""); }}>✓ Completar</button>
										<button className="crm-b chico" onClick={() => { setReagendar(reagendar === a.id ? null : a.id); setCompletar(null); setRFecha(manana()); setRH({ modo: null, hora: "" }); }}>Reagendar</button>
									</div>
								</div>
								{completar === a.id ? (
									<div className="crm-completar">
										<b>¿Cómo salió?</b>
										<div className="crm-tipos" role="group" aria-label="Cómo salió">
											{RESULTADOS.map((r) => <button key={r.key} className={"crm-tipo" + (resultado === r.key ? " on" : "")} aria-pressed={resultado === r.key} onClick={() => setResultado(r.key)}>{r.label}</button>)}
										</div>
										<div className="crm-form"><div className="ancho"><label htmlFor={"act-nota-" + a.id}>Nota (opcional)</label>
											<input id={"act-nota-" + a.id} value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Confirmó el sábado" /></div></div>
										{resultado === "no_interesa" ? <div className="crm-pie">Su status pasa a Potencial · “no le interesa por ahora”.</div> : null}
										<div className="crm-botones">
											<button className="crm-b morado" disabled={busy} onClick={() => completarYa(a, false)}>Completar</button>
											{resultado !== "no_interesa" ? <button className="crm-b" disabled={busy} onClick={() => completarYa(a, true)}>Completar y agendar la siguiente</button> : null}
											<button className="crm-b" onClick={() => setCompletar(null)}>Cancelar</button>
										</div>
									</div>
								) : null}
								{reagendar === a.id ? (
									<div className="crm-completar">
										<div className="crm-form">
											<div><label htmlFor={"re-f-" + a.id}>Nueva fecha</label><input id={"re-f-" + a.id} type="date" value={rFecha} onChange={(e) => setRFecha(e.target.value)} /></div>
											<div className="ancho"><PasoHora value={rH} onChange={setRH} idBase={"re-" + a.id} fecha={rFecha} /></div>
										</div>
										<div className="crm-botones">
											<button className="crm-b morado" disabled={busy || !rFecha || !horaCompleta(rH, rFecha)}
												onClick={() => hacer(() => http.prospectsService.reagendarActividad(campusId, prospectId, a.id, armarActividad(a.tipo, rFecha, rH)))}>Guardar</button>
											<button className="crm-b" onClick={() => setReagendar(null)}>Cancelar</button>
										</div>
									</div>
								) : null}
							</React.Fragment>
						);
					})}
					{!data.abiertas.length ? <div className="crm-pie">No tiene actividades abiertas.</div> : null}
				</>
			) : (
				<>
					{data.cerradas.map((a) => (
						<div key={a.id} className="crm-it">
							<div className="crm-ico tarea" aria-hidden="true">{a.resultado === "cancelada" ? "✕" : "✓"}</div>
							<div className="tx">
								<b>{a.tipoLabel}</b>{a.titulo ? " · " + a.titulo : ""}{a.resultadoLabel ? " — " + a.resultadoLabel : ""}
								{a.nota ? <div>{a.nota}</div> : null}
								<div className="meta">
									{a.done_at ? fechaDia(a.done_at) + " · " + hora(a.done_at) : ""}{a.done_by_name || a.owner_name ? " · " + (a.done_by_name || a.owner_name) : ""}
									{a.origen === "zoho" ? <span className="crm-orig">Zoho</span> : null}
								</div>
							</div>
						</div>
					))}
					{data.totalCerradas > data.cerradas.length ? <div className="crm-pie">Se muestran las {data.cerradas.length} más recientes de {data.totalCerradas.toLocaleString("es-MX")}.</div> : null}
					{!data.cerradas.length ? <div className="crm-pie">Todavía no hay actividades cerradas.</div> : null}
				</>
			)}
			<p className="crm-pie">Las tareas de Zoho llegaron como actividades, abiertas o cerradas como estaban. Las vencidas salen en dorado y las de hoy en morado.</p>
		</div>
	);
};

export default CrmActividades;
